import { useState, useCallback, useEffect, useMemo } from 'react';
import { useExpensesService } from 'services/expenses';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Expense } from 'types/expenses';

const EXPENSES_CACHE_KEY_BASE = 'expenses_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const getExpensesCacheKey = (
  includeVoided: boolean,
  companyId: string | null | undefined,
) => {
  const baseKey = `${EXPENSES_CACHE_KEY_BASE}_${
    includeVoided ? 'withVoided' : 'withoutVoided'
  }`;
  return getCacheKeyWithCompany(baseKey, companyId);
};

const sortExpensesByDateDesc = (expenses: Expense[]) =>
  [...expenses].sort((a, b) => {
    const aTime = new Date(a.date).getTime();
    const bTime = new Date(b.date).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseExpensesDataOptions {
  includeVoided: boolean;
}

interface UseExpensesDataReturn {
  expenses: Expense[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  updateExpense: (expense: Expense) => void;
}

export function useExpensesData(
  options: UseExpensesDataOptions,
): UseExpensesDataReturn {
  const { includeVoided } = options;
  const expensesService = useExpensesService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const expensesCacheKey = useMemo(
    () => getExpensesCacheKey(includeVoided, activeCompanyId),
    [includeVoided, activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedExpenses,
    setData: setCachedExpenses,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Expense[]>({
    key: expensesCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  // Reset cache check when includeVoided or company changes (cache key changes)
  useEffect(() => {
    setCacheChecked(false);
    setLoading(true);
    // Clear expenses temporarily while loading new cache
    setExpenses([]);
    // Reset error state
    setError(null);
  }, [includeVoided, activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready

    if (cachedExpenses) {
      // Use cached data - it's still valid (hook already checked expiration)
      setExpenses(sortExpensesByDateDesc(cachedExpenses));
      setLoading(false);
      setCacheChecked(true);
    } else {
      // No cache or expired - mark as checked so we can fetch
      setCacheChecked(true);
    }
  }, [
    cachedExpenses,
    cacheInitialized,
    isCompanyInitialized,
    expensesCacheKey,
  ]);

  const fetchExpenses = useCallback(
    async (isReload = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const fetchedExpenses =
          await expensesService.getAllExpenses(includeVoided);
        const sortedExpenses = sortExpensesByDateDesc(fetchedExpenses);
        setExpenses(sortedExpenses);

        // Limit cached expenses to first 20 to avoid large data issues
        const expensesToCache =
          sortedExpenses.length > 20
            ? sortedExpenses.slice(0, 20)
            : sortedExpenses;

        // Save to cache with encryption
        await setCachedExpenses(expensesToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch expenses:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load expenses. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      includeVoided,
      expensesService,
      setCachedExpenses,
      isCompanyInitialized,
      activeCompanyId,
    ],
  );

  // Only fetch data if cache is checked, company is initialized, and no cached data exists
  useEffect(() => {
    if (
      cacheChecked &&
      cacheInitialized &&
      isCompanyInitialized &&
      !cachedExpenses
    ) {
      void fetchExpenses();
    }
  }, [
    cacheChecked,
    cacheInitialized,
    isCompanyInitialized,
    cachedExpenses,
    fetchExpenses,
    includeVoided,
  ]);

  const reload = useCallback(() => {
    void fetchExpenses(true);
  }, [fetchExpenses]);

  const updateExpense = useCallback(
    (updatedExpense: Expense) => {
      setExpenses((prevExpenses) => {
        const nextExpenses = prevExpenses.map((expense) =>
          expense.id === updatedExpense.id ? updatedExpense : expense,
        );

        const sortedExpenses = sortExpensesByDateDesc(nextExpenses);

        // Update cache with encrypted data (fire and forget)
        void setCachedExpenses(sortedExpenses);

        return sortedExpenses;
      });
    },
    [setCachedExpenses],
  );

  return {
    expenses,
    loading,
    reloading,
    error,
    reload,
    updateExpense,
  };
}
